/**
 * Human-readable OE Condition IDs — `YY-SSS-NNNN`, e.g. `26-HOU-0024` (Beta-1 plan P2-06, ⚑Q-15
 * adopted 09-12): two-digit year of submission, a 3-letter SITE CODE (auto-derived from the site
 * name, editable in Settings → Organization → Sites, unique per organization) and a per
 * org + site + year sequence. Pure module (no Firebase) so it can be unit-tested; the counter
 * transaction lives in trlService.allocateConditionId.
 */

const STOP_WORDS = new Set(['THE', 'OF', 'AND', 'PLANT', 'SITE', 'FACILITY', 'CENTER', 'CENTRE', 'HQ', 'INC', 'LLC', 'CO']);

function lettersOnly(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, ' ')
    .trim();
}

/**
 * Derive a 3-character code from a site name: the first three letters of the first meaningful
 * word ("Houston HQ" → HOU, "Brasília Plant" → BRA, "St. Louis" → STL when the first word is
 * too short, "Kalamazoo R&D Center" → KAL). Falls back to initials, then to
 * padding with X. `taken` (existing codes in the org) forces a unique variant: HOU → HO2 → HO3…
 */
export function deriveSiteCode(name, taken = []) {
  const words = lettersOnly(name).split(/\s+/).filter((w) => w && !STOP_WORDS.has(w));
  let base = '';
  if (words.length && words[0].length >= 3) base = words[0];
  else if (words.length) base = words.join(''); // "St. Louis" → STLOUIS → STL
  else base = lettersOnly(name).replace(/\s+/g, '');
  base = (base + 'XXX').slice(0, 3);
  const used = new Set((taken || []).map((c) => String(c).toUpperCase()));
  if (!used.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base.slice(0, n < 10 ? 2 : 1)}${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

/** True for a usable site code: 2–4 letters/digits, uppercase. */
export function isValidSiteCode(code) {
  return /^[A-Z0-9]{2,4}$/.test(String(code || ''));
}

/** The code to use for a site: its stored `code` when valid, else derived from its name (unique vs. the org's other sites). */
export function siteCodeFor(site, organization) {
  if (!site) return 'ORG';
  if (isValidSiteCode(site.code)) return String(site.code).toUpperCase();
  const others = (organization?.sites || []).filter((s) => s.id !== site.id).map((s) => (isValidSiteCode(s.code) ? s.code : deriveSiteCode(s.name)));
  return deriveSiteCode(site.name, others);
}

/** Two-digit year of a Date / Firestore Timestamp / millis. */
export function yearCode(at = new Date()) {
  const d = at && typeof at.toDate === 'function' ? at.toDate() : at instanceof Date ? at : new Date(at);
  return String(d.getFullYear() % 100).padStart(2, '0');
}

export function formatConditionId(yy, siteCode, seq) {
  return `${yy}-${String(siteCode).toUpperCase()}-${String(seq).padStart(4, '0')}`;
}

/** Counter document id for one org + site + year (Hugo_Counters/{...}). */
export function counterKey(orgId, siteCode, yy) {
  return `${orgId}_${String(siteCode).toUpperCase()}_${yy}`;
}

export function parseConditionId(id) {
  const m = /^(\d{2})-([A-Z0-9]{2,4})-(\d{4,})$/.exec(String(id || '').toUpperCase());
  if (!m) return null;
  return { yy: m[1], siteCode: m[2], seq: Number(m[3]) };
}

/** Sort key so `26-HOU-0002` < `26-HOU-0010` and years/sites group naturally. */
export function compareConditionIds(a, b) {
  const pa = parseConditionId(a);
  const pb = parseConditionId(b);
  if (!pa || !pb) return String(a || '').localeCompare(String(b || ''));
  return pa.yy.localeCompare(pb.yy) || pa.siteCode.localeCompare(pb.siteCode) || pa.seq - pb.seq;
}
